import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  MessagingChannelReplyMode,
  MessagingChannelType,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import AutocompleteMessagingRecipientsTypeahead from '~components/FormFields/CommonAutocompletes/AutocompleteMessagingRecipientsTypeahead';
import Label from '~components/FormFields/v2/Label';
import TextInput from '~components/FormFields/v2/TextInput';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import Tabs, { TabProps } from '~components/Tabs/Tabs';
import { MAX_BROADCAST_RECIPIENTS } from '~hooks/useMessaging/messagingRecipients';
import { MessagingChannel } from '~hooks/useMessaging/models';
import useStartConversation, {
  MessagingRecipientsUnreachableError,
} from '~hooks/useMessaging/useStartConversation';
import BroadcastPreview from '~pages/Messages/components/BroadcastPreview';
import BroadcastRecipientsField from '~pages/Messages/components/BroadcastRecipientsField';
import {
  messagingChannelTypeLabel,
  messagingReplyModeLabel,
  messagingReplyModeTag,
} from '~pages/Messages/messagingChannelLabels';
import { useStores } from '~store';
import { LabeledOption } from '~types/LabeledOption';
import {
  getEffectiveUserCompanyId,
  getEffectiveUserCompanyName,
} from '~utils/user/user-utils';

/**
 * A job or order conversation is started from its own row, so this dialog offers only the
 * two types that have no subject to pick.
 */
type ComposableChannelType =
  | typeof MessagingChannelType.BROADCAST
  | typeof MessagingChannelType.DIRECT;

/** The autocomplete stores the whole option, not its id: one for single-select, an array for multi-select. */
type NewConversationFormValues = {
  channelType: ComposableChannelType;
  message: string;
  name: string;
  recipient: LabeledOption | null;
  recipients: LabeledOption[];
  replyMode: MessagingChannelReplyMode;
};

const emptyForm: NewConversationFormValues = {
  channelType: MessagingChannelType.DIRECT,
  message: '',
  name: '',
  recipient: null,
  recipients: [],
  replyMode: MessagingChannelReplyMode.FORUM,
};

type NewConversationDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (channel: MessagingChannel) => void;
};

const NewConversationDialog = observer(
  ({ isOpen, onClose, onCreated }: NewConversationDialogProps) => {
    const theme = useTheme();
    const { userStore } = useStores();
    // Not the account selector's company: the direct picker's recipients are session scoped, so
    // its caption must compare against the company the server actually scoped by.
    const ownCompanyId = userStore.userCompany.id;
    // A broadcast is scoped to the company being dispatched as, so its captions are too.
    const actingCompanyId = getEffectiveUserCompanyId(userStore);
    const actingCompanyName = getEffectiveUserCompanyName(userStore);
    const actingCompany = useMemo(
      () => ({ id: actingCompanyId, name: actingCompanyName }),
      [actingCompanyId, actingCompanyName],
    );
    const { startConversation } = useStartConversation();
    const [isPreviewing, setIsPreviewing] = useState(false);
    const [isStarting, setIsStarting] = useState(false);
    const [unreachableRecipientIds, setUnreachableRecipientIds] = useState<string[]>([]);

    const { control, handleSubmit, setValue, watch } = useForm<NewConversationFormValues>(
      {
        defaultValues: emptyForm,
      },
    );

    const channelType = watch('channelType');
    const message = watch('message');
    const recipient = watch('recipient');
    const recipients = watch('recipients');
    const replyMode = watch('replyMode');

    const isBroadcast = channelType === MessagingChannelType.BROADCAST;

    // The api refuses a direct channel to yourself, so you are not offered as a recipient.
    const selfExcludedFromRecipients = userStore.user?.id
      ? [userStore.user.id]
      : undefined;

    // The labels list needs manage_labels, which some users who can broadcast lack.
    const canFilterRecipientsByLabel = userStore.getAccess().manageLabels;

    const canCreateBroadcast = Boolean(
      userStore.getPermissions()?.canCreateMessagingBroadcast,
    );

    const channelTypeTabs: TabProps<ComposableChannelType>[] = useMemo(
      () => [
        {
          label: messagingChannelTypeLabel(MessagingChannelType.DIRECT),
          value: MessagingChannelType.DIRECT,
        },
        {
          label: messagingChannelTypeLabel(MessagingChannelType.BROADCAST),
          value: MessagingChannelType.BROADCAST,
        },
      ],
      [],
    );

    const replyModeTabs: TabProps<MessagingChannelReplyMode>[] = useMemo(
      () =>
        Object.values(MessagingChannelReplyMode).map((mode) => ({
          label: messagingReplyModeTag(mode),
          value: mode,
        })),
      [],
    );

    const isSubjectChosen = isBroadcast ? recipients.length > 0 : Boolean(recipient);

    // A hand-grown selection can pass the cap, and the api rejects the whole broadcast.
    const isWithinRecipientLimit =
      !isBroadcast || recipients.length <= MAX_BROADCAST_RECIPIENTS;

    const selectedRecipientIds = useMemo(
      () => new Set(recipients.map((option) => option.value)),
      [recipients],
    );
    const selectedUnreachableRecipientIds = unreachableRecipientIds.filter((userId) =>
      selectedRecipientIds.has(userId),
    );
    if (selectedUnreachableRecipientIds.length !== unreachableRecipientIds.length) {
      setUnreachableRecipientIds(selectedUnreachableRecipientIds);
    }

    const hasUnreachableRecipients =
      isBroadcast && selectedUnreachableRecipientIds.length > 0;

    const canSubmit =
      isSubjectChosen &&
      isWithinRecipientLimit &&
      !hasUnreachableRecipients &&
      (!isBroadcast || Boolean(message.trim())) &&
      !isStarting;

    const submit = handleSubmit(async (values) => {
      const selectedRecipients = isBroadcast ? values.recipients : [values.recipient];

      setIsStarting(true);

      try {
        const channel = await startConversation({
          channelType: values.channelType,
          initialMessage: isBroadcast ? values.message : undefined,
          name: values.name,
          recipientUserIds: selectedRecipients
            .filter((option): option is LabeledOption => option !== null)
            .map((option) => option.value),
          replyMode: isBroadcast ? values.replyMode : undefined,
          scopedCompanyId: actingCompanyId || undefined,
        });

        onCreated(channel);
      } catch (error) {
        if (!(error instanceof MessagingRecipientsUnreachableError)) throw error;

        setUnreachableRecipientIds(error.userIds);
        setIsPreviewing(false);
      } finally {
        setIsStarting(false);
      }
    });

    // A broadcast cannot be recalled, so it is confirmed once before it goes.
    const onPrimaryAction = useCallback(
      (event: React.FormEvent) => {
        event.preventDefault();
        event.stopPropagation();

        if (isBroadcast && !isPreviewing) {
          setIsPreviewing(true);
          return;
        }

        // react-hook-form re-throws; the failure is already toasted where it happened.
        void submit().catch(() => {});
      },
      [isBroadcast, isPreviewing, submit],
    );

    return (
      <Dialog isOpen={isOpen} onClose={onClose} preventCloseOnBackdropClick>
        <form noValidate autoComplete="off" onSubmit={onPrimaryAction}>
          <DialogHeader onClose={onClose}>{t('messaging.new_message')}</DialogHeader>

          <DialogContent>
            {isPreviewing ? (
              <BroadcastPreview
                message={message}
                recipientCount={recipients.length}
                replyMode={replyMode}
              />
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                {canCreateBroadcast && (
                  <Box data-test-id="new-conversation-channel-type">
                    <Label>{t('messaging.channel_type')}</Label>

                    <Tabs
                      onChange={(value) => setValue('channelType', value)}
                      selected={channelType}
                      tabs={channelTypeTabs}
                    />

                    <FieldHint
                      text={
                        isBroadcast
                          ? t('messaging.channel_type_broadcast_hint')
                          : t('messaging.channel_type_direct_hint')
                      }
                    />
                  </Box>
                )}

                {isBroadcast ? (
                  <BroadcastRecipientsField
                    actingCompany={actingCompany}
                    control={control}
                    canFilterByLabel={canFilterRecipientsByLabel}
                    name="recipients"
                    unreachableRecipientIds={unreachableRecipientIds}
                  />
                ) : (
                  <Box>
                    <AutocompleteMessagingRecipientsTypeahead
                      control={control}
                      excludedOptionValues={selfExcludedFromRecipients}
                      isRequired
                      label={t('messaging.recipient')}
                      name="recipient"
                      ownCompanyId={ownCompanyId}
                    />

                    <FieldHint text={t('messaging.recipient_hint')} />
                  </Box>
                )}

                {isBroadcast && (
                  <Box
                    sx={{
                      border: `solid 1px ${theme.semantic.divider}`,
                      borderRadius: theme.brandV2.borderRadius,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      p: 1.5,
                    }}
                  >
                    <Typography variant="subtitle2">
                      {t('messaging.reply_mode')}
                    </Typography>

                    <Box data-test-id="new-conversation-reply-mode">
                      <Tabs
                        onChange={(value) => setValue('replyMode', value)}
                        selected={replyMode}
                        tabs={replyModeTabs}
                      />
                    </Box>

                    <Typography
                      sx={{ color: theme.semantic.textSecondary }}
                      variant="caption"
                    >
                      {messagingReplyModeLabel(replyMode)}
                    </Typography>
                  </Box>
                )}

                <Box>
                  <TextInput
                    control={control}
                    label={t('messaging.conversation_name')}
                    name="name"
                  />

                  <FieldHint text={t('messaging.conversation_name_hint')} />
                </Box>

                {isBroadcast && (
                  <TextInput
                    control={control}
                    isRequired
                    label={t('messaging.message')}
                    name="message"
                    rows={4}
                  />
                )}
              </Box>
            )}
          </DialogContent>

          <DialogActions>
            <SmallButton
              color="secondary"
              disabled={isStarting}
              onClick={isPreviewing ? () => setIsPreviewing(false) : onClose}
              variant="outlined"
            >
              {isPreviewing ? t('actions.back') : t('actions.cancel')}
            </SmallButton>

            <SmallLoadingButton
              color="brandV2Yellow"
              disabled={!canSubmit}
              loading={isStarting}
              type="submit"
            >
              {isBroadcast && !isPreviewing
                ? t('messaging.review_broadcast')
                : t('messaging.start_conversation')}
            </SmallLoadingButton>
          </DialogActions>
        </form>
      </Dialog>
    );
  },
);

function FieldHint({ text }: { text: string }) {
  const theme = useTheme();

  return (
    <Typography
      sx={{ color: theme.semantic.textSecondary, display: 'block', mt: 0.5 }}
      variant="caption"
    >
      {text}
    </Typography>
  );
}

export default NewConversationDialog;
